import type { Theme } from '@mui/material/styles';

// ----------------------------------------------------------------------

/**
 * Deliberately almost empty. The document-level globals live elsewhere:
 *
 *   - `assets/scss/main.scss` owns `html`, `body` and `#root` — including the
 *     `--mfw-window-height` / `100dvh` sizing that survives the iOS toolbar
 *     collapse;
 *   - Tailwind preflight owns the resets (`* { box-sizing }`, `body { margin }`)
 *     and the block-image baseline (`img { display: block }`);
 *   - MUI's own CssBaseline defaults add `box-sizing` and `body { margin: 0 }`
 *     on top, whether or not anything is declared here.
 *
 * This override must not restate any of them: `<CssBaseline />` renders with
 * no `StyledEngineProvider injectFirst`, so Emotion appends these rules AFTER
 * the static stylesheet, and at equal specificity the later rule wins. When
 * this file first went live it did exactly that — `#root { height: 100% }`
 * replaced the viewport-height workaround and `img { display: inline-block }`
 * undid preflight app-wide. `__tests__/createAppTheme.spec.ts` pins the
 * ownership.
 */
export function cssBaseline(_theme: Theme) {
  return {
    MuiCssBaseline: {
      styleOverrides: {
        // Input chrome, not a document global, and nothing above competes
        // with it (preflight only sets the spin buttons' `height`).
        // `assets/scss/theme.scss` carries the same declarations in
        // `@layer base`, so the two agree rather than fight.
        input: {
          '&[type=number]': {
            '&::-webkit-inner-spin-button': {
              WebkitAppearance: 'none',
              margin: 0,
            },
            '&::-webkit-outer-spin-button': {
              WebkitAppearance: 'none',
              margin: 0,
            },
            MozAppearance: 'textfield',
          },
        },
      },
    },
  };
}
